/**
 * MyGovernmentMessagesPage — /government/my-messages
 *
 * Citizen dashboard showing every message they've submitted to any
 * department, with live status and any owner response.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Inbox, MessageSquare, ArrowRight, ShieldCheck, Landmark } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import GovernmentCategoryNav from "@/components/GovernmentCategoryNav";

const STATUS_COLORS = {
  received:    { label: "🟢 Received",    cls: "bg-green-50 text-green-800" },
  under_review:{ label: "🟡 Under review", cls: "bg-amber-50 text-amber-800" },
  in_progress: { label: "🔵 In progress",  cls: "bg-blue-50 text-blue-800" },
  completed:   { label: "✅ Completed",    cls: "bg-emerald-50 text-emerald-800" },
  closed:      { label: "⚪ Closed",       cls: "bg-slate-50 text-slate-700" },
};

export default function MyGovernmentMessagesPage() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) { toast.error("Sign in to see your messages."); nav(`/login?redirect=${encodeURIComponent("/government/my-messages")}`, { replace: true }); return; }
    if (user) {
      setLoading(true);
      http.get("/government/messages/mine")
        .then(r => setItems(r.data.items || []))
        .finally(() => setLoading(false));
    }
  }, [user, authLoading, nav]);

  return (
    <>
      <GovernmentCategoryNav />
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="my-messages-page">
        <div className="overline mb-1">MY MESSAGES</div>
        <h1 className="font-display text-4xl mb-2 inline-flex items-center gap-2"><Inbox className="w-8 h-8" />Your government messages</h1>
        <p className="opacity-70 mb-6">Track everything you've submitted through Ask Your Government.</p>

        {loading ? (
          <div className="opacity-60 inline-flex gap-1.5"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center">
            <MessageSquare className="w-8 h-8 mx-auto opacity-40 mb-3" />
            <p className="opacity-70 mb-3">You haven't sent any messages yet.</p>
            <Link to="/government/departments" className="wofy-btn-primary inline-flex items-center gap-1.5">
              Browse departments <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map(m => {
              const s = STATUS_COLORS[m.status] || STATUS_COLORS.received;
              return (
                <li key={m.id} className="wofy-card !p-4" data-testid={`msg-${m.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
                        <Landmark className="w-3 h-3 inline mr-1" />
                        <Link to={`/government/departments/${m.department_slug}`} className="wofy-link">{m.department_name}</Link>
                        <span className="ml-2 opacity-70">· {m.category} · {m.kind}</span>
                      </div>
                      <div className="font-medium mt-0.5">{m.subject}</div>
                      <p className="text-sm opacity-80 whitespace-pre-line mt-1">{m.description}</p>
                      {m.owner_response && (
                        <div className="mt-3 p-3 rounded" style={{ background: "var(--wofy-surface)" }} data-testid={`response-${m.id}`}>
                          <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 inline-flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" />OFFICIAL RESPONSE
                          </div>
                          <p className="text-sm whitespace-pre-line opacity-90 mt-1">{m.owner_response}</p>
                        </div>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${s.cls}`}>{s.label}</span>
                  </div>
                  <div className="mt-3">
                    <details className="text-xs">
                      <summary className="cursor-pointer opacity-70">Status history ({m.status_history?.length || 0})</summary>
                      <ol className="mt-2 space-y-1">
                        {(m.status_history || []).map((h, i) => (
                          <li key={i} className="opacity-80">
                            <span className="font-mono">{new Date(h.at).toLocaleString()}</span>
                            {" · "}<b>{h.status}</b>
                            {h.note ? ` — ${h.note}` : ""}
                          </li>
                        ))}
                      </ol>
                    </details>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}
